import { PublicClientApplication, type AccountInfo, type AuthenticationResult, type Configuration, type PopupRequest, type SilentRequest } from "@azure/msal-browser";
import type { User } from '@microsoft/microsoft-graph-types';
import type { UserProfileImage } from "~/constants/types/UserProfileImage";


export default defineNuxtPlugin(async () => {
  const { authority, clientId, redirectUri } = await $fetch('/api/auth/msal-credentials');
  const config = ref<Configuration>({
    auth:
        {
          clientId,
          authority,
          redirectUri,
        },
  });

  const msal = ref<PublicClientApplication>(new PublicClientApplication(config.value));

  const token = ref<AuthenticationResult | null>(null);

  await msal.value.initialize();

  const login = async () => {
    try {
      const loginRequest: PopupRequest = {
        scopes: ['user.read'],
      };
      const loginResponse = await msal.value.loginPopup(loginRequest);

      // Set the active account after login
      msal.value.setActiveAccount(loginResponse.account);

      return loginResponse;
    }
    catch (err) {
      console.error(err);
    }
  }

  const acquireTokenSilent = async (): Promise<AuthenticationResult | null> => {
    const activeAccount: AccountInfo | null = msal.value.getActiveAccount();

    if (!activeAccount) return null; // No active account found

    const tokenRequest: SilentRequest = {
      scopes: ['user.read'],
      account: activeAccount,
    };

    token.value = await msal.value.acquireTokenSilent(tokenRequest);
    return token.value;
  };

  const profileInfo = async (): Promise<User | null> => {
    if (token.value == null) return null;

    const payload = await $fetch<User>('https://graph.microsoft.com/v1.0/me', {
      headers: {
        Authorization: `Bearer ${token.value?.accessToken}`,
      },
    });
    return payload;
  }

  const profileImg = async (): Promise<UserProfileImage> => {
    try {
      if (token.value == null) throw new Error('No token response available. Failed to fetch profile image.');

      const imageResponse = await $fetch<Blob | null>(
        `https://graph.microsoft.com/v1.0/me/photo/$value`,
        {
          headers: {
            Authorization: `Bearer ${token.value.accessToken}`,
          },
        }
      );

      if (imageResponse == null) throw new Error('Failed to fetch profile image');

      const imageUrl = URL.createObjectURL(imageResponse);
      return { data: imageUrl, error: null }
    }
    catch (error) {
      return { data: null, error: new Error(error as string) }
    }
  }


  const logout = async (): Promise<boolean> => {
    const token = await acquireTokenSilent();

    if (token == null) {
      console.error('No token response available. Failed to logout.');
      await navigateTo('/login');
      return false;
    }

    const currentAccount: AccountInfo | null = msal.value.getAccount(token.account);
    await msal.value.logoutRedirect({ account: currentAccount });
    return true;
  }

  return {
    provide: {
      login,
      acquireTokenSilent,
      profileInfo,
      profileImg,
      logout
    }
  }
});
